import { Icon, Spinner, Tag, Text } from "@ttc3k/trekker";
import {
  ActivityLogManyQuery,
  EnumActivityLogType,
  EnumSurveyResponseAdminStatus,
  EnumSurveyResponseReviewStatus,
  EnumSurveyResponseStatus,
} from "gql/generated";
import { Box, Flex, styled } from "styled-system/jsx";
import { ActivityFeedButton } from "./ActivityFeedButton";
import { ArrowRight } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { formatDate, getActivityLogCardData, DATE_PATTERN_STANDARD } from "shared/utils";
import { useMemo } from "react";

const FORM_LOG_TYPES: EnumActivityLogType[] = [
  EnumActivityLogType.FormCompleted,
  EnumActivityLogType.FormDraftSaved,
  EnumActivityLogType.FormResubmitted,
  EnumActivityLogType.FormSubmissionApproved,
  EnumActivityLogType.FormSubmissionRejected,
];

type ActivityFeedCardProps = {
  activityLog: ActivityLogManyQuery["activityLogMany"][0];
  onClick: (activityLog: ActivityLogManyQuery["activityLogMany"][0]) => void;
  loadingItemId?: string | null;
  showDate?: boolean;
  tagLookup?: Map<string, string>;
};

export const ActivityFeedCard = ({
  activityLog,
  onClick,
  loadingItemId,
  showDate,
  tagLookup,
}: ActivityFeedCardProps) => {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.ACTIVITY_FEED" });
  const { t: tPeople } = useTranslation("people", { keyPrefix: "FORM_RESPONSE_MODAL" });
  const data = getActivityLogCardData(activityLog, t);
  const { title, boldedText, cta, visual, titleFormat, hideAction } = data;

  const isUnread =
    activityLog.surveyResponse?.adminStatus === EnumSurveyResponseAdminStatus.Unread;

  const isFormLog = FORM_LOG_TYPES.includes(activityLog.type);

  const inquiryTags = useMemo(() => {
    if (!isFormLog) return [];
    const tags: { label: string; visual: string }[] = [];

    const reviewStatus = activityLog.surveyResponse?.reviewStatus;
    const status = activityLog.surveyResponse?.status;

    if (reviewStatus === EnumSurveyResponseReviewStatus.Approved) {
      tags.push({ label: tPeople("REVIEW_APPROVED"), visual: "success" });
    } else if (reviewStatus === EnumSurveyResponseReviewStatus.Rejected) {
      tags.push({ label: tPeople("REVIEW_REJECTED"), visual: "warning" });
    } else if (status === EnumSurveyResponseStatus.Submitted) {
      tags.push({ label: tPeople("SUBMITTED"), visual: "success" });
    } else if (status === EnumSurveyResponseStatus.PendingReview) {
      tags.push({ label: tPeople("PENDING_REVIEW"), visual: "warning" });
    } else if (status === EnumSurveyResponseStatus.Draft) {
      tags.push({ label: tPeople("DRAFT"), visual: "primary" });
    }

    const customTagIds = activityLog.surveyResponse?.tags ?? activityLog.tags ?? [];
    if (tagLookup) {
      for (const tagId of customTagIds) {
        if (!tagId) continue;
        const name = tagLookup.get(tagId);
        if (name) tags.push({ label: name, visual: "accent.purple" });
      }
    }
    return tags;
  }, [isFormLog, activityLog, tagLookup, tPeople]);

  const displayTitle = (title && title.trim()) || "";
  if (!displayTitle && !boldedText) {
    if (process.env.NODE_ENV === "development") {
      return (
        <Flex
          py={"200"}
          px={"250"}
          bg={"red.50"}
          border={"1px solid"}
          borderColor={"red.200"}
          borderRadius={"100"}
          width={"full"}
          gap={"200"}
        >
          <Text visual="smallRegular" color="red.600">
            [DEBUG] Empty card — type: <styled.span fontWeight="semiBold">{String(activityLog.type ?? "null")}</styled.span>
            {" | "}_id: {activityLog._id}
            {" | "}message: {JSON.stringify(activityLog.message)}
            {" | "}entityName: {JSON.stringify(activityLog.entityName)}
          </Text>
        </Flex>
      );
    }
    return null;
  }

  const titleContent = (
    <Text lineClamp={2}>
      {titleFormat === "eventSubmitted" && displayTitle.includes(",") ? (
        <>
          {displayTitle.split(",")[0]}&nbsp;
          <styled.span fontWeight={"semiBold"}>{boldedText}</styled.span>
          &nbsp;{displayTitle.split(",")[1]}
        </>
      ) : titleFormat === "operatorAdded" ? (
        <>
          {displayTitle.split(" to Directory")[0]}&nbsp;
          <styled.span fontWeight={"semiBold"}>{boldedText}</styled.span>
          &nbsp;to Directory
        </>
      ) : (
        <>
          {displayTitle}
          {boldedText ? (
            <>
              &nbsp;
              <styled.span fontWeight={"semiBold"}>{boldedText}</styled.span>
            </>
          ) : null}
        </>
      )}
    </Text>
  );

  return (
    <Flex
      py={"200"}
      px={"250"}
      bg={"white"}
      border={"1px solid"}
      borderColor={"border.light"}
      borderRadius={"100"}
      alignItems={"center"}
      gap={"300"}
      justifyContent={"space-between"}
      width={"full"}
    >
      <Flex alignItems="center" gap="200" minW="0" flex="1">
        {isUnread && (
          <Box
            w="8px"
            h="8px"
            borderRadius="full"
            bgColor="bg.accent.magenta.mid"
            flexShrink={0}
          />
        )}
        <Flex flexDir="column" minW="0" gap="50">
          {titleContent}
          {showDate && (
            <Text visual="smallRegular" color="text.light" fontSize="3xs">
              {formatDate(activityLog.createdDateISO, DATE_PATTERN_STANDARD)}
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex alignItems="center" gap="150" flexShrink={0}>
        {inquiryTags.length > 0 && (
          <Flex gap="75" alignItems="center" flexShrink={0}>
            {inquiryTags.map((tag, idx) => (
              <Tag
                key={idx}
                visual={tag.visual as any}
                mode="light"
                size="sm"
              >
                <styled.span whiteSpace="nowrap">{tag.label}</styled.span>
              </Tag>
            ))}
          </Flex>
        )}
        {!hideAction && cta && (
          <ActivityFeedButton
            className={"group"}
            visual={visual}
            onClick={() => onClick(activityLog)}
          >
            {cta}
            {loadingItemId === activityLog._id ? (
              <Spinner width={"20px"} height={"20px"} />
            ) : (
              <styled.div
                transitionDuration={"normal"}
                transitionProperty={"transform"}
                transitionTimingFunction={"default"}
                _groupHover={{ transform: "translateX(4px)" }}
                _groupFocusVisible={{ transform: "translateX(4px)" }}
              >
                <Icon Element={ArrowRight} />
              </styled.div>
            )}
          </ActivityFeedButton>
        )}
      </Flex>
    </Flex>
  );
};
